import OperationalState, { collectionAllowed } from "@/components/OperationalState";
import { useRead } from "@/components/LoadingStates";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { storedCollection, submitCollection, refreshCollection, collectionScope } from "@/lib/collectionOperations";
import CollectionOperationState from "@/components/CollectionOperationState";

export const CREATOR_DIALOG_CLASS = "left-0 top-0 flex h-[100dvh] w-full max-w-none translate-x-0 translate-y-0 flex-col overflow-y-auto rounded-none sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg";

const isIngestStatus = (data) => data && Array.isArray(data.providers)
  && data.providers.every((p) => p && typeof p.platform === "string" && typeof p.connected === "boolean")
  && data.operations && typeof data.operations.private === "boolean"
  && typeof data.operations.status === "string" && typeof data.operations.reason === "string"
  && typeof data.operations.paused === "boolean" && typeof data.operations.closing === "boolean"
  && typeof data.operations.effective?.manual_collection === "boolean"
  && (data.operations.private ? ["ready", "missing", "invalid", "unavailable"].includes(data.operations.status) : data.operations.status === "standard")
  && (!data.operations.effective.manual_collection || (!data.operations.paused && !data.operations.closing && ["ready", "standard"].includes(data.operations.status)));

export default function AddCreatorDialog({ children, onCreated, open: controlledOpen, onOpenChange, returnFocusRef }) {
  const { canEdit, user } = useAuth();
  const [operation, setOperation] = useState(null);
  const [operationError, setOperationError] = useState("");
  const activeScope = useRef(collectionScope(user));
  activeScope.current = collectionScope(user);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen === undefined ? internalOpen : controlledOpen;
  const setOpen = (next) => { setInternalOpen(next); onOpenChange?.(next); };
  const [platform, setPlatform] = useState("youtube");
  const [handle, setHandle] = useState("");
  const [niche, setNiche] = useState("");
  const [adding, setAdding] = useState(false);
  // A new open must obtain its own status, even if the previous open succeeded.
  const statusScope = useRef({ open: false, version: 0 });
  if (statusScope.current.open !== open) statusScope.current = { open, version: statusScope.current.version + 1 };
  const statusRead = useRead(open && canEdit ? "/ingest/status" : null, isIngestStatus, String(statusScope.current.version));
  const status = statusRead.data;
  const statusKnown = statusRead.status === "success" && !statusRead.isRefreshing && !statusRead.refreshError;
  const statusLoading = statusRead.status === "loading" || statusRead.isRefreshing;
  const collectionEnabled = statusKnown && status.operations.effective.manual_collection === true && collectionAllowed(status.operations, platform);
  const submitting = useRef(false);
  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;


  const finish = async (data) => {
    setOperation(data.operation);
    if (data.operation.state !== "completed") return;
    toast.success(`Ingested ${data.videos_ingested} videos from ${data.creator.handle} (${data.outliers} outliers)`);
    setHandle("");
    setOpen(false);
    try { await onCreatedRef.current?.(data.creator); }
    catch (error) { toast.error(`Creator added, but the list could not be refreshed: ${formatError(error)}`); }
  };
  const refreshOperation = async () => {
    const scope = activeScope.current;
    setAdding(true);
    try {
      const data = await refreshCollection(user, "add");
      if (scope === activeScope.current) { setOperationError(""); await finish(data); }
    } catch (error) { if (scope === activeScope.current) setOperationError(formatError(error)); }
    finally { setAdding(false); }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!canEdit || !collectionEnabled || submitting.current || !handle.trim() || handle.length > 100) return;
    submitting.current = true;
    setAdding(true);
    const scope = activeScope.current;
    try {
      const data = await submitCollection(user, "add", "/ingest/creators", { platform, handle, niche: niche || "General" });
      if (scope === activeScope.current) { setOperationError(""); await finish(data); }
    } catch (error) {
      if (scope === activeScope.current) {
        try { const saved = storedCollection(user, "add"); setOperation(saved ? { ...saved, ...(error?.response?.data?.operation || {}) } : null); } catch {}
        setOperationError(formatError(error));
      }
      toast.error(formatError(error));
    } finally {
      submitting.current = false;
      setAdding(false);
    }
  };

  if (!canEdit) return null;
  const connected = status?.providers?.find((provider) => provider.platform === platform)?.connected;

  return (
    <Dialog open={open} onOpenChange={(next) => {
      if (next) {
        setOperation(null); setOperationError("");
        try {
          const saved = storedCollection(user, "add");
          if (saved && !["completed", "abandoned"].includes(saved.state)) {
            setOperation(saved); setPlatform(saved.parameters.platform); setHandle(saved.parameters.handle); setNiche(saved.parameters.niche);
          }
        } catch (error) { setOperationError(formatError(error)); }
      }
      setOpen(next);
    }}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className={CREATOR_DIALOG_CLASS} data-testid="add-creator-dialog" onCloseAutoFocus={(event) => {
        if (returnFocusRef?.current?.isConnected) { event.preventDefault(); returnFocusRef.current.focus(); }
      }}>
        <DialogHeader>
          <DialogTitle>Add Creator</DialogTitle>
          <DialogDescription>Track a live creator and fetch their latest short-form videos.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" aria-busy={adding}>
          <div className="space-y-1.5">
            <label htmlFor="ingest-platform" className="text-sm font-medium">Platform</label>
            <Select value={platform} onValueChange={setPlatform} disabled={adding}>
              <SelectTrigger id="ingest-platform" data-testid="ingest-platform-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="youtube" data-testid="ingest-platform-youtube">YouTube Shorts</SelectItem>
                <SelectItem value="tiktok" data-testid="ingest-platform-tiktok">TikTok</SelectItem>
                <SelectItem value="instagram" data-testid="ingest-platform-instagram">Instagram Reels</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="ingest-handle" className="text-sm font-medium">Handle</label>
            <Input id="ingest-handle" value={handle} onChange={(event) => setHandle(event.target.value)} placeholder="@handle" required maxLength={100} disabled={adding} data-testid="ingest-handle-input" />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="ingest-niche" className="text-sm font-medium">Niche (optional)</label>
            <Input id="ingest-niche" value={niche} onChange={(event) => setNiche(event.target.value)} placeholder="e.g. Fitness" disabled={adding} data-testid="ingest-niche-input" />
          </div>
          {statusKnown && <OperationalState state={status.operations} capabilities={["manual_collection"]} scope="add-creator" />}
          {statusKnown && status.operations.private && <Button type="button" variant="outline" data-testid="add-creator-limits-retry" onClick={() => statusRead.reload().catch(() => {})}>Actualiser les allocations (lecture seule)</Button>}
          {statusKnown && !collectionEnabled && !status.operations.private && <p role="status" className="text-sm">{status.operations.reason}</p>}
          {!statusKnown && <div role={statusLoading ? "status" : "alert"} className="rounded-lg border p-3 text-sm space-y-2" data-testid="add-creator-operational-state" data-state={statusLoading ? "loading" : "unavailable"}>
            <p id="add-creator-status-explanation">{statusLoading
              ? "Vérification des autorisations de collecte… La soumission reste désactivée."
              : "Impossible de vérifier les autorisations de collecte. La soumission reste désactivée ; vos champs sont conservés."}</p>
            {!statusLoading && <Button type="button" variant="outline" data-testid="add-creator-status-retry" onClick={() => statusRead.reload().catch(() => {})}>Réessayer le statut</Button>}
          </div>}
          {status && !connected && (
            <p className="text-sm text-[hsl(var(--amber))]" data-testid="ingest-not-connected-hint">
              Paramètres fournisseur absents côté serveur pour cette plateforme. Consultez leur état dans <Link to="/sources" className="underline">Integrations</Link>.
            </p>
          )}
          <CollectionOperationState operation={operation} error={operationError} onRefresh={refreshOperation} busy={adding} scope="add-creator" />
          <Button type="submit" disabled={adding || !collectionEnabled || !handle.trim()} aria-describedby={!statusKnown ? "add-creator-status-explanation" : undefined} data-testid="add-creator-submit" className="w-full sm:w-auto">
            {adding ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />Fetching…</> : "Add Creator"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
